import React, { useRef, useEffect } from 'react';
import { NodeViewWrapper } from '@tiptap/react';

const ResizablePDF = ({ node, updateAttributes }) => {
  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const startWidth = useRef(0);
  const startHeight = useRef(0);

  useEffect(() => {
    const container = containerRef.current;
    const frame = iframeRef.current;
    if (!container || !frame) return;

    const handleMouseDown = (e) => {
      const isHandle = (e.target.classList && e.target.classList.contains('resize-handle')) ||
        (typeof e.target.closest === 'function' && !!e.target.closest('.resize-handle'));
      if (!isHandle) return;
      e.preventDefault();
      e.stopPropagation();
      isResizing.current = true;
      startX.current = e.clientX;
      startY.current = e.clientY;
      startWidth.current = frame.offsetWidth;
      startHeight.current = frame.offsetHeight;
    };

    const handleMouseMove = (e) => {
      if (!isResizing.current) return;
      e.preventDefault();
      const dx = e.clientX - startX.current;
      const dy = e.clientY - startY.current;
      const newW = Math.max(200, startWidth.current + dx);
      const newH = Math.max(160, startHeight.current + dy);
      updateAttributes({ width: `${newW}px`, height: `${newH}px` });
      frame.style.width = `${newW}px`;
      frame.style.height = `${newH}px`;
    };

    const handleMouseUp = () => {
      isResizing.current = false;
    };

    container.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      container.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [updateAttributes]);

  const handleInsertAbove = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (containerRef.current) {
      const editor = containerRef.current.closest('.ProseMirror')?.__editor;
      if (editor) {
        const pos = editor.view.posAtDOM(containerRef.current, 0);
        editor.commands.setTextSelection(pos);
        editor.commands.focus();
      }
    }
  };

  const handleInsertBelow = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (containerRef.current) {
      const editor = containerRef.current.closest('.ProseMirror')?.__editor;
      if (editor) {
        const pos = editor.view.posAtDOM(containerRef.current, 1);
        editor.commands.setTextSelection(pos);
        editor.commands.focus();
      }
    }
  };

  return (
    <NodeViewWrapper 
      style={{
        margin: '0',
        padding: '0',
        display: 'block'
      }}
    >
      <div className="embed-insert-zone top" onMouseDown={handleInsertAbove} />
      <div 
        ref={containerRef}
        style={{
          display: 'inline-block',
          margin: '0',
          padding: '0',
          position: 'relative'
        }}
      >
        <iframe
          ref={iframeRef}
          src={(node.attrs.url || '') + '#toolbar=0&navpanes=0&scrollbar=0'}
          title="PDF Viewer"
          width={node.attrs.width || '100%'}
          height={node.attrs.height || '400'}
          style={{
            border: '1.5px solid #e0e0e0',
            borderRadius: '12px',
            boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
            margin: '0',
            padding: '0',
            display: 'block'
          }}
          allow="autoplay"
        />
        <a
          href={node.attrs.url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#8a2be2',
            fontWeight: '500',
            textDecoration: 'underline',
            margin: '0',
            padding: '0'
          }}
        >
          {node.attrs.filename || 'Open PDF'}
        </a>
        <div 
          className="resize-handle"
          style={{
            position: 'absolute',
            bottom: '-2px',
            right: '-25px',
            width: '18px',
            height: '18px',
            background: 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.2) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '6px',
            border: '1px solid rgba(255,255,255,0.3)',
            boxShadow: '0 2px 8px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.25)',
            cursor: 'se-resize',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(0,0,0,0.7)',
            fontSize: '11px',
            fontWeight: 'bold',
            zIndex: 10,
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            const el = e.currentTarget;
            el.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.2) 50%, rgba(255,255,255,0.4) 100%)';
            el.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            const el = e.currentTarget;
            el.style.background = 'linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.2) 100%)';
            el.style.transform = 'scale(1)';
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true" focusable="false" style={{ transform: 'scaleX(-1)', transformOrigin: '50% 50%', pointerEvents: 'none' }}>
            <path fillRule="evenodd" d="M5.828 10.172a.5.5 0 0 0-.707 0l-4.096 4.096V11.5a.5.5 0 0 0-1 0v3.975a.5.5 0 0 0 .5.5H4.5a.5.5 0 0 0 0-1H1.732l4.096-4.096a.5.5 0 0 0 0-.707m4.344-4.344a.5.5 0 0 0 .707 0l4.096-4.096V4.5a.5.5 0 1 0 1 0V.525a.5.5 0 0 0-.5-.5H11.5a.5.5 0 0 0 0 1h2.768l-4.096 4.096a.5.5 0 0 0 0 .707"/>
          </svg>
        </div>
      </div>
      <div className="embed-insert-zone bottom" onMouseDown={handleInsertBelow} />
    </NodeViewWrapper>
  );
};

export default ResizablePDF;
